import React, { useState, useRef } from 'react';
import { MARKETPLACE_ALERT_RANGE, todayMatches, WINE_ALERT_RANGE } from '../../lib/dealInsights';

// Shared Wine Tracker Plus alert settings: fields, validation and defaults used by
// the setup flow (SetupAccount) and the account page (EmailAlerts).

export const WINE_TRACKER_PLUS_LABEL = 'Wine Tracker Plus';

export const DEFAULT_ALERT_SETTINGS = {
  email: '',
  phone: '',
  wine_alerts: false,
  sms_alerts: false,
  wine_threshold: '2.0',
  // Matches the account default the server sets on sign-up and assumes when unset.
  same_wine_day_interval: '7',
};

export const DEFAULT_MARKETPLACE_SETTINGS = {
  marketplace_email_alerts: false,
  marketplace_sms_alerts: false,
  marketplace_threshold: '2.0',
  marketplace_repeat_interval: '7',
};

// How soon the same deal can alert again, for wine and Marketplace alike.
export const REPEAT_INTERVAL_OPTIONS = [
  { value: '7', label: 'Every 7 days' },
  { value: '14', label: 'Every 14 days' },
  { value: '30', label: 'Every 30 days' },
  { value: 'never', label: 'Never repeat' },
];

const repeatIntervalOptions = () => REPEAT_INTERVAL_OPTIONS.map(option => (
  <option key={option.value} value={option.value}>{option.label}</option>
));

export const WINE_THRESHOLD_OPTIONS = Array.from({ length: 11 }, (_, i) => (1.5 + i * 0.1).toFixed(1));

const PHONE_EXAMPLE = 'For example, 0412 345 678.';
export const PHONE_ERROR = 'Please enter a valid Australian mobile number.';
export const PHONE_SMS_ERROR = 'Please enter a valid Australian mobile number to enable SMS alerts.';
export const EMAIL_ALERT_ERROR = 'Please enter a valid email address before enabling email alerts.';

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ''));
}

// Store the national format expected by the account API and existing SMS sender.
export function normalisePhoneInput(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (/^614\d{8}$/.test(digits)) return `0${digits.slice(2)}`;
  if (/^4\d{8}$/.test(digits)) return `0${digits}`;
  return digits;
}

export function isValidPhone(value) {
  return /^04\d{8}$/.test(normalisePhoneInput(value));
}

export function editableAlertSettings(settings, marketplace = false) {
  const keys = [...Object.keys(DEFAULT_ALERT_SETTINGS), 'phoneCountryCode',
    ...(marketplace ? Object.keys(DEFAULT_MARKETPLACE_SETTINGS) : [])];
  return Object.fromEntries(keys.filter(key => Object.prototype.hasOwnProperty.call(settings, key))
    .map(key => [key, key === 'phone' ? normalisePhoneInput(settings[key]) : settings[key]]));
}

export async function settingsSaveError(response, fallback) {
  if (response.status === 401) return 'Your session has expired. Please sign in again, then save your settings.';
  if (response.status === 429) return 'Too many save attempts. Please wait a moment and try again.';
  if (response.status >= 400 && response.status < 500) {
    const data = await response.json().catch(() => null);
    if (typeof data?.error === 'string' && data.error.length <= 300) return data.error;
  }
  return fallback;
}

export function AlertSwitch({ id, name, label, checked, disabled = false, onToggle }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked ? 'true' : 'false'}
      aria-label={label}
      className="ff-switch"
      disabled={disabled}
      onClick={() => onToggle(name, !checked)}
    />
  );
}

function SwitchRow({ name, label, description, checked, disabled, onToggle }) {
  return (
    <div className="ff-alert-switch-row">
      <div className="ff-alert-switch-row__copy">
        <span className="ff-field__label">{label}</span>
        {description ? <p className="ff-field__hint">{description}</p> : null}
      </div>
      <div className="ff-switch-row">
        <AlertSwitch name={name} label={label} checked={checked} disabled={disabled} onToggle={onToggle} />
      </div>
    </div>
  );
}

/**
 * Renders the alert settings fields. State stays with the caller so each container can keep
 * its own metadata, identity and persistence rules.
 *
 * onInputChange(name, value) is called for text inputs and selects.
 * onToggle(name, checked) is called for switches and checkboxes.
 */
export function AlertSettingsFields({
  formData,
  onInputChange,
  onToggle,
  phoneError = '',
  development = false,
  headingLevel = 2,
  copy = {},
}) {
  const Heading = `h${headingLevel}`;
  const emailValid = isValidEmail(formData.email);
  const phoneValid = isValidPhone(formData.phone);
  const text = {
    contactTitle: 'Contact details',
    emailHint: "We'll send wine deal alerts to this email address.",
    phoneHint: 'Optional. Needed only for SMS alerts, which are available for Australian mobiles.',
    channelsTitle: development ? 'Wine alert channels' : 'Alert channels',
    channelsLead: 'Alerts are sent only on the channels you switch on.',
    emailAlertsDescription: 'Receive wine deal notifications by email.',
    smsAlertsDescription: 'Get instant notifications by text message.',
    settingsTitle: development ? 'Wine deal settings' : 'Deal alert settings',
    thresholdHint: 'You’ll be alerted to deals at or below this value. Lower values mean better deals, and fewer alerts.',
    intervalHint: 'If a deal is still available, how long to wait before alerting you about it again.',
    ...copy,
  };

  return (
    <>
      <section className="ff-card ff-card--static ff-card__body ff-alert-section">
        <Heading className="ff-alert-section__title">{text.contactTitle}</Heading>
        <div className="ff-alert-section__fields">
          <div className="ff-field">
            <label htmlFor="email" className="ff-field__label">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              className="ff-input"
              value={formData.email}
              onChange={event => onInputChange('email', event.target.value)}
              autoComplete="email"
              required
            />
            <p className="ff-field__hint">{text.emailHint}</p>
          </div>

          <div className="ff-field">
            <label htmlFor="phone" className="ff-field__label">Mobile</label>
            <div className="ff-input-group">
              <span className="ff-input-group__prefix" aria-hidden="true">+61</span>
              <input
                type="tel"
                id="phone"
                name="phone"
                className={`ff-input${phoneError ? ' ff-input--invalid' : ''}`}
                value={formData.phone}
                onChange={event => onInputChange('phone', normalisePhoneInput(event.target.value))}
                placeholder="0412 345 678"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength="24"
                aria-describedby={phoneError ? 'phone-error phone-hint' : 'phone-hint'}
                aria-invalid={phoneError ? 'true' : undefined}
              />
            </div>
            {phoneError ? <p id="phone-error" className="ff-field__error">{phoneError}</p> : null}
            <p id="phone-hint" className="ff-field__hint">{text.phoneHint} {PHONE_EXAMPLE}</p>
          </div>
        </div>
      </section>

      <section className="ff-card ff-card--static ff-card__body ff-alert-section">
        <Heading className="ff-alert-section__title">{text.channelsTitle}</Heading>
        <p className="ff-field__hint">{text.channelsLead}</p>
        <div className="ff-alert-section__fields">
          <SwitchRow
            name="wine_alerts"
            label="Email alerts"
            description={emailValid ? text.emailAlertsDescription : 'Enter a valid email address to enable email alerts.'}
            checked={Boolean(formData.wine_alerts)}
            disabled={!emailValid}
            onToggle={onToggle}
          />
          <SwitchRow
            name="sms_alerts"
            label="SMS alerts"
            description={phoneValid ? text.smsAlertsDescription : 'Enter a valid mobile number to enable SMS alerts.'}
            checked={Boolean(formData.sms_alerts)}
            disabled={!phoneValid}
            onToggle={onToggle}
          />
        </div>
      </section>

      <section className="ff-card ff-card--static ff-card__body ff-alert-section">
        <Heading className="ff-alert-section__title">{text.settingsTitle}</Heading>
        <div className="ff-alert-section__fields">
          <div className="ff-field">
            <label htmlFor="wine_threshold" className="ff-field__label">Wine deal threshold</label>
            <select
              id="wine_threshold"
              name="wine_threshold"
              className="ff-select"
              value={formData.wine_threshold}
              onChange={event => onInputChange('wine_threshold', event.target.value)}
            >
              {WINE_THRESHOLD_OPTIONS.map(value => (
                <option key={value} value={value}>
                  {value} cents per point ({(100 / parseFloat(value)).toFixed(1)} points per dollar)
                </option>
              ))}
            </select>
            <p className="ff-field__hint">{text.thresholdHint}</p>
          </div>

          <div className="ff-field">
            <label htmlFor="same_wine_day_interval" className="ff-field__label">Repeat alert interval</label>
            <select
              id="same_wine_day_interval"
              name="same_wine_day_interval"
              className="ff-select"
              value={formData.same_wine_day_interval}
              onChange={event => onInputChange('same_wine_day_interval', event.target.value)}
            >
              {repeatIntervalOptions()}
            </select>
            <p className="ff-field__hint">{text.intervalHint}</p>
          </div>
        </div>
      </section>

      {development ? (
        <section className="ff-card ff-card--static ff-card__body ff-alert-section">
          <Heading className="ff-alert-section__title">Marketplace alerts</Heading>
          <p className="ff-field__hint">Choose each channel explicitly. Entering contact details does not enable alerts.</p>
          <div className="ff-alert-section__fields">
            <SwitchRow
              name="marketplace_email_alerts"
              label="Marketplace email alerts"
              description={emailValid ? 'Marketplace deals by email.' : 'Enter a valid email address to enable email alerts.'}
              checked={Boolean(formData.marketplace_email_alerts)}
              disabled={!emailValid}
              onToggle={onToggle}
            />
            <SwitchRow
              name="marketplace_sms_alerts"
              label="Marketplace SMS alerts"
              description={phoneValid ? 'Marketplace deals by text message.' : 'Enter a valid mobile number to enable SMS alerts.'}
              checked={Boolean(formData.marketplace_sms_alerts)}
              disabled={!phoneValid}
              onToggle={onToggle}
            />
            <div className="ff-field">
              <label htmlFor="marketplace_threshold" className="ff-field__label">Marketplace deal threshold (cents per point)</label>
              <input
                type="number"
                id="marketplace_threshold"
                name="marketplace_threshold"
                className="ff-input"
                min="0.1"
                max="20"
                step="0.1"
                value={formData.marketplace_threshold}
                onChange={event => onInputChange('marketplace_threshold', event.target.value)}
                required
              />
              <p className="ff-field__hint">Counts bonus plus base points. Lower values mean better deals, and fewer alerts.</p>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}

export default AlertSettingsFields;


// The account's threshold control for both sources: − / value / +, in 0.1¢ steps within
// the source's range. A typed value is tidied to one decimal (and into range) on leaving.
function ThresholdStepper({ id, name, value, range, onChange, describedBy }) {
  // The visible label is a short "Alert at"; the name says which source it sets.
  const current = Number(value);
  const clamp = number => Math.min(range.max, Math.max(range.min, Math.round(number * 10) / 10)).toFixed(1);
  const step = delta => onChange(clamp((Number.isFinite(current) ? current : range.min) + delta));
  return <div className="ff-alert-stepper">
    <button type="button" className="ff-alert-stepper__button" onClick={() => step(-0.1)} disabled={current <= range.min} aria-label={`Lower the ${name.toLowerCase()} threshold`}>−</button>
    <span className="ff-alert-stepper__value">
      <input id={id} type="number" inputMode="decimal" className="ff-alert-stepper__input" min={range.min} max={range.max} step="0.1"
        value={value} onChange={event => onChange(event.target.value)}
        onBlur={event => { const typed = Number(event.target.value); if (event.target.value !== '' && Number.isFinite(typed) && clamp(typed) !== value) onChange(clamp(typed)); }}
        aria-label={`${name} deal threshold`} aria-describedby={describedBy} />
      <span className="ff-alert-stepper__unit" aria-hidden="true">¢</span>
    </span>
    <button type="button" className="ff-alert-stepper__button" onClick={() => step(0.1)} disabled={current >= range.max} aria-label={`Raise the ${name.toLowerCase()} threshold`}>+</button>
  </div>;
}

// One line under a source's controls: how today's deals compare with its threshold
// (amber when none qualify), then what the number means.
// "3 of today's 73 deals qualify · Best today 2.26¢ · Lower is better; wine counts bonus points only."
function SourceHint({ id, cents, threshold, note }) {
  const today = todayMatches(cents, threshold);
  return <p id={id} className="ff-alert-source__hint" aria-live="polite">
    {today && <>
      <strong className={today.matching ? 'ff-alert-today--some' : 'ff-alert-today--none'}>
        {today.matching ? `${today.matching} of today’s ${today.total} deals qualify` : `None of today’s ${today.total} deals qualify`}
      </strong>
      {' · '}Best today {today.best.toFixed(2)}¢{' · '}
    </>}
    {note}
  </p>;
}

const SOURCE_DETAILS = {
  wine: {
    name: 'Wine', title: 'Qantas Wine', channels: ['wine_alerts', 'sms_alerts'], threshold: 'wine_threshold', repeat: 'same_wine_day_interval', range: WINE_ALERT_RANGE,
    note: 'Lower is better. Wine alerts count bonus points only, so they read a little higher than the tracker.',
  },
  marketplace: {
    name: 'Marketplace', title: 'Qantas Marketplace', channels: ['marketplace_email_alerts', 'marketplace_sms_alerts'], threshold: 'marketplace_threshold', repeat: 'marketplace_repeat_interval', range: MARKETPLACE_ALERT_RANGE,
    note: 'Lower is better. Marketplace alerts count bonus and base points.',
  },
};

// Where alerts go first (each source's channels depend on it), then one block per
// source: its switch and a one-line summary, then one row of controls (threshold,
// channels, repeat) and one line comparing the threshold with today's deals. `today` holds each
// source's current cents per point (useTodayDeals); setup retains its existing fields.
export function AccountAlertFields({ formData, onInputChange, onToggle, onChannelsChange, phoneError, marketplace, contactStatus, mobileVerification, initiallyOpen = null, today = null }) {
  const [opened, setOpened] = useState(() => (initiallyOpen ? { [initiallyOpen]: true } : {}));
  const remembered = useRef({});
  const toggleSource = (source, enabled) => {
    const keys = SOURCE_DETAILS[source].channels;
    if (!enabled) {
      remembered.current[source] = Object.fromEntries(keys.map(key => [key, Boolean(formData[key])]));
      onChannelsChange(Object.fromEntries(keys.map(key => [key, false])));
    } else if (remembered.current[source]) {
      const restored = { ...remembered.current[source] };
      if (!isValidEmail(formData.email) || contactStatus?.suppressions?.email) restored[keys[0]] = false;
      if (!isValidPhone(formData.phone) || contactStatus?.suppressions?.sms) restored[keys[1]] = false;
      onChannelsChange(restored);
    }
    setOpened(previous => ({ ...previous, [source]: enabled }));
  };
  return <>
    <section className="ff-alert-contact" aria-label="Send alerts to">
      <h3 className="ff-alert-section__title">Send alerts to</h3>
      <div className="ff-alert-contact__fields">
        <div className="ff-field">
          <label htmlFor="email" className="ff-field__label">Email</label>
          <input id="email" name="email" type="email" className="ff-input" autoComplete="email" value={formData.email} onChange={event => onInputChange('email', event.target.value)} />
          {contactStatus?.suppressions?.email && <p className="ff-field__error">Email alerts are stopped by your email provider.</p>}
        </div>
        <div className="ff-field">
          <label htmlFor="phone" className="ff-field__label">Mobile</label>
          <div className="ff-phone-input">
            <span className="ff-phone-input__prefix" aria-hidden="true"><span className="ff-phone-input__flag">🇦🇺</span> +61</span>
            <input id="phone" name="phone" type="tel" className={`ff-input${phoneError ? ' ff-input--invalid' : ''}`} autoComplete="tel-national" inputMode="tel" placeholder="0412 345 678" value={formData.phone} onChange={event => onInputChange('phone', normalisePhoneInput(event.target.value))} aria-invalid={phoneError ? 'true' : undefined} aria-describedby={phoneError ? 'phone-error' : undefined} />
          </div>
          {phoneError && <p id="phone-error" className="ff-field__error">{phoneError}</p>}
          {mobileVerification}
        </div>
      </div>
    </section>
    <div className="ff-alert-sources">
      {['wine', ...(marketplace ? ['marketplace'] : [])].map(source => {
        const details = SOURCE_DETAILS[source];
        const { name } = details;
        const [emailKey, smsKey] = details.channels;
        const enabled = opened[source] ?? Boolean(formData[emailKey] || formData[smsKey]);
        const channels = [formData[emailKey] && 'Email', formData[smsKey] && 'SMS'].filter(Boolean).join(' + ');
        const threshold = Number(formData[details.threshold]);
        const summary = !enabled ? 'Off' : `${channels || 'No channel chosen'}${Number.isFinite(threshold) ? ` · ${threshold.toFixed(1)}¢ or less` : ''}`;
        return <section className="ff-alert-source" key={source} aria-label={`${name} alerts`}>
          <div className="ff-alert-switch-row">
            <h3 className="ff-alert-section__title">{details.title}</h3>
            <span className={`ff-alert-source__summary${enabled ? ' is-on' : ''}`}>{summary}</span>
            <AlertSwitch name={source} label={`${name} alerts`} checked={enabled} onToggle={toggleSource} />
          </div>
          {enabled && <div className="ff-alert-source__options">
            <div className="ff-alert-source__controls">
              <div className="ff-field">
                <label htmlFor={details.threshold} className="ff-field__label">Alert at</label>
                <div className="ff-alert-source__threshold">
                  <ThresholdStepper id={details.threshold} name={name} value={formData[details.threshold]} range={details.range}
                    onChange={value => onInputChange(details.threshold, value)} describedBy={`${source}-hint`} />
                  <span className="ff-alert-stepper__after">or less</span>
                </div>
              </div>
              <div className="ff-field">
                <span className="ff-field__label" id={`${source}-channels-label`}>Send by</span>
                <div className="ff-alert-channels" role="group" aria-labelledby={`${source}-channels-label`}>
                  {[['email', emailKey, 'Email', isValidEmail(formData.email)], ['sms', smsKey, 'SMS', isValidPhone(formData.phone)]].map(([channel, key, label, valid]) => {
                    const blocked = contactStatus?.suppressions?.[channel] === true;
                    return <label key={channel} className="ff-alert-channel">
                      <input type="checkbox" checked={Boolean(formData[key])} disabled={!formData[key] && (!valid || blocked)} onChange={event => { setOpened(previous => ({ ...previous, [source]: true })); onToggle(key, event.target.checked); }} aria-label={`${name} ${label} alerts`} />
                      <span>{label}</span>
                    </label>;
                  })}
                </div>
              </div>
              <div className="ff-field">
                <label htmlFor={details.repeat} className="ff-field__label">Repeat</label>
                <select id={details.repeat} className="ff-select" aria-label={`${name} repeat interval`} value={formData[details.repeat]} onChange={event => onInputChange(details.repeat, event.target.value)}>
                  {repeatIntervalOptions()}
                </select>
              </div>
            </div>
            {[['email', 'Email', isValidEmail(formData.email), 'an email'], ['sms', 'SMS', isValidPhone(formData.phone), 'a mobile']].map(([channel, label, valid, what]) => (
              contactStatus?.suppressions?.[channel] ? <p key={channel} className="ff-field__error">{label} stopped by provider</p>
                : !valid ? <p key={channel} className="ff-field__hint">For {label}, add {what} above.</p> : null
            ))}
            <SourceHint id={`${source}-hint`} cents={today?.[source]} threshold={formData[details.threshold]} note={details.note} />
          </div>}
        </section>;
      })}
    </div>
  </>;
}
